import { read, utils } from 'xlsx';
import type { PulseImport, PulseImportRequest } from '@/types';

/** Most tickets one import may name or reach; machine, jobs and kim-ai-backend cap at the same number. */
export const PULSE_IMPORT_MAX_TICKETS = 5000;
export const DEFAULT_MAX_TICKETS = 500;

/** A file the operator dropped in: its header row and its rows as text. */
export interface TicketIdSheet {
  columns: string[];
  rows: Array<Record<string, string>>;
}

/**
 * A cell as text. Excel stores long ids as numbers and formats them as `2.15477E+14`, so a
 * whole number is written out digit by digit instead of read back through its format.
 */
function cellText(value: unknown): string {
  if (typeof value === 'number' && Number.isInteger(value)) return value.toFixed(0);
  return String(value ?? '');
}

/**
 * Reads a CSV or Excel file in the browser. Only the first sheet is read. CSV cells stay
 * text (`raw: true` stops SheetJS guessing numbers), and Excel's numeric ids keep all their
 * digits.
 */
export async function readTicketIdSheet(file: File): Promise<TicketIdSheet> {
  const workbook = read(await file.arrayBuffer(), { type: 'array', raw: true });
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!firstSheet) return { columns: [], rows: [] };
  const rows = utils.sheet_to_json<Record<string, unknown>>(firstSheet, { defval: '', raw: true });
  const header = utils.sheet_to_json<unknown[]>(firstSheet, { header: 1, raw: true })[0] ?? [];
  return {
    columns: header.map((column) => cellText(column).trim()).filter(Boolean),
    rows: rows.map((row) =>
      Object.fromEntries(Object.entries(row).map(([key, value]) => [key.trim(), cellText(value)]))
    )
  };
}

/** Column names helpdesk exports use for the ticket id, most specific first. */
const ID_COLUMN_PATTERNS = [
  /^conversation id$/i,
  /ticket id$/i,
  /conversation (id|number)$/i,
  /^ticket$/i,
  /^id$/i
];

/** The column most likely to hold the ticket id, or the first column. */
export function guessIdColumn(columns: string[]): string | null {
  for (const pattern of ID_COLUMN_PATTERNS) {
    const match = columns.find((column) => pattern.test(column));
    if (match) return match;
  }
  return columns[0] ?? null;
}

/** The ids in one column, trimmed, blanks dropped, each once, in file order. */
export function ticketIdsFromColumn(sheet: TicketIdSheet, column: string): string[] {
  return uniqueIds(sheet.rows.map((row) => row[column] ?? ''));
}

/** Ids pasted as text: one per line, or separated by commas, spaces or tabs. */
export function ticketIdsFromText(text: string): string[] {
  return uniqueIds(text.split(/[\s,;]+/));
}

function uniqueIds(values: string[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    const id = value.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

/** A local calendar date as `YYYY-MM-DD`, the way the date picker shows it. */
export function toCalendarDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** The operator's time zone: the dates they pick are their own calendar days. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

export interface FiltersForm {
  buId: number | null;
  startDate?: Date;
  endDate?: Date;
  intents: string[];
  maxCount: number;
}

export interface IdsForm {
  ticketIds: string[];
  maxCount: number;
}

export type BuiltRequest = { request: PulseImportRequest } | { error: string };

function maxCountError(maxCount: number): string | null {
  return Number.isInteger(maxCount) && maxCount >= 1 && maxCount <= PULSE_IMPORT_MAX_TICKETS
    ? null
    : `Max tickets must be a whole number from 1 to ${PULSE_IMPORT_MAX_TICKETS.toLocaleString()}.`;
}

/** The filters-tab request, or why it cannot be sent yet. */
export function buildFiltersRequest(form: FiltersForm, timeZone = browserTimeZone()): BuiltRequest {
  if (form.buId == null) return { error: 'Choose a brand.' };
  if (!form.startDate || !form.endDate) return { error: 'Choose the closed dates.' };
  const countError = maxCountError(form.maxCount);
  if (countError) return { error: countError };
  return {
    request: {
      mode: 'filters',
      bu_id: form.buId,
      start_date: toCalendarDate(form.startDate),
      end_date: toCalendarDate(form.endDate),
      time_zone: timeZone,
      intents: form.intents,
      max_count: form.maxCount
    }
  };
}

/** The ticket-ids-tab request, or why it cannot be sent yet. */
export function buildIdsRequest(form: IdsForm): BuiltRequest {
  if (form.ticketIds.length === 0) return { error: 'Add a file or paste ticket IDs.' };
  if (form.ticketIds.length > PULSE_IMPORT_MAX_TICKETS) {
    return {
      error: `That's ${form.ticketIds.length.toLocaleString()} IDs. One import takes up to ${PULSE_IMPORT_MAX_TICKETS.toLocaleString()}.`
    };
  }
  const countError = maxCountError(form.maxCount);
  if (countError) return { error: countError };
  return { request: { mode: 'ids', ticket_ids: form.ticketIds, max_count: form.maxCount, intents: [] } };
}

export const isImportRunning = (pulseImport: Pick<PulseImport, 'status'>) =>
  pulseImport.status === 'queued' || pulseImport.status === 'running';

/** One line on what an import asked for. */
export function describeImport(pulseImport: PulseImport): string {
  const { filters } = pulseImport;
  const parts: string[] = [];
  if (pulseImport.mode === 'ids' && filters.intents.length === 0) {
    parts.push(`${pulseImport.counts.requested.toLocaleString()} ticket IDs`);
  }
  if (filters.intents.length > 0) parts.push(filters.intents.join(', '));
  if (filters.start_date && filters.end_date) {
    parts.push(`closed ${filters.start_date} to ${filters.end_date}`);
  }
  parts.push(`max ${filters.max_count.toLocaleString()}`);
  return parts.join(' · ');
}

/** One line on what an import did. */
export function summarizeImport(pulseImport: PulseImport): string {
  const { counts } = pulseImport;
  const parts = [`${counts.emitted.toLocaleString()} published`];
  if (counts.deduped) parts.push(`${counts.deduped.toLocaleString()} already in Pulse`);
  if (counts.skipped) parts.push(`${counts.skipped.toLocaleString()} skipped`);
  if (counts.failed) parts.push(`${counts.failed.toLocaleString()} failed`);
  if (counts.other_intent) parts.push(`${counts.other_intent.toLocaleString()} other intent`);
  if (counts.unclassified) parts.push(`${counts.unclassified.toLocaleString()} unclassified`);
  if (pulseImport.status === 'completed' || pulseImport.status === 'failed') {
    parts.push(`${counts.insights_succeeded.toLocaleString()} insights`);
  }
  return parts.join(' · ');
}
